// Pure decisions for the Web Push service worker (service-worker.ts), kept free of
// the worker globals so they unit-test without one.

export interface PushPayload {
  type?: string;
  title?: string;
  body?: string;
  url?: string;
  tag?: string;
  workspaceId?: string;
  kind?: string;
  channelId?: string;
  dmPeerId?: string;
  messageId?: string;
  threadRootId?: string;
  activityId?: string;
  // {type:"clear"}: the activityIds whose delivered notifications to close.
  ids?: string[];
  // {type:"clear"} / activity_read_changed: whole conversations read; close by channel too.
  channelIds?: string[];
  badge?: number;
  // A relay that predates the top-level fields nests them here and sends `badgeCount`.
  data?: Partial<PushPayload>;
  badgeCount?: number;
}

// Top-level first; `data.*` / `badgeCount` only fill what a not-yet-redeployed relay left out.
export function normalizePushPayload(raw: PushPayload): PushPayload {
  const { data, badgeCount, ...top } = raw;
  const out: PushPayload = { ...data, ...stripUndefined(top) };
  if (typeof out.badge !== "number" && typeof badgeCount === "number") out.badge = badgeCount;
  return out;
}

function stripUndefined<T extends object>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;
}

// An activity-row tag collapses by row; channel:/dm: tags repeat per conversation, so the next
// message must still alert (renotify) instead of silently replacing the last one.
export function shouldRenotify(p: PushPayload): boolean {
  return !!p.tag && !p.activityId;
}

// The app's own deep-link shape: channelNotificationUrl / the DM banner in app.svelte.ts
// (`?m=` lands on the message) and `?thread=` (thread-url-sync.ts) opens the pane.
// An agent DM (or an agent error in a DM) opens the agent route the server put in `url`
// (/workspace/<ws>/agent/<id>), which also honours `?m=`; /dm/<peer> is the wrong page for it.
function isAgentDmPush(p: PushPayload): boolean {
  if (!p.url) return false;
  // A permission push carries the cybo id as dmPeerId, but its approval card lives at `url`.
  return (
    p.kind === "agent_dm_received" ||
    p.kind === "schedule_needs_input" ||
    p.kind === "permission" ||
    (p.kind === "agent_error" && !!p.dmPeerId && !p.channelId)
  );
}

function conversationPath(ws: string | undefined, p: PushPayload): string | undefined {
  if (ws && p.channelId) return `/workspace/${ws}/channel/${p.channelId}`;
  if (ws && p.dmPeerId) return `/workspace/${ws}/dm/${p.dmPeerId}`;
  return undefined;
}

export function pushTargetUrl(p: PushPayload): string {
  const ws = p.workspaceId;
  if (isAgentDmPush(p) && p.url) {
    const path = p.url.split("?")[0];
    return p.messageId ? `${path}?m=${encodeURIComponent(p.messageId)}` : path;
  }
  const base = conversationPath(ws, p);
  if (!base) return p.url || "/";
  const q: string[] = [];
  if (p.messageId) q.push(`m=${encodeURIComponent(p.messageId)}`);
  if (p.threadRootId) q.push(`thread=${encodeURIComponent(p.threadRootId)}`);
  return q.length ? `${base}?${q.join("&")}` : base;
}

// The in-app notification already covers a window the user is looking at.
export function hasFocusedVisibleWindow(
  clients: ReadonlyArray<{ focused?: boolean; visibilityState?: string }>,
): boolean {
  return clients.some((c) => c.focused === true && c.visibilityState === "visible");
}

// A channel clear closes only `channel:<id>`-tagged banners: a thread_reply banner carries
// data.channelId but is a separate unread, so it must survive.
export function notificationsToClose<T extends { tag?: string; data?: unknown }>(
  shown: readonly T[],
  ids: readonly string[] | undefined,
  channelIds?: readonly string[],
): T[] {
  const set = new Set(ids ?? []);
  const channels = new Set(channelIds ?? []);
  return shown.filter((n) => {
    if (n.tag && set.has(n.tag)) return true;
    if (!channels.size) return false;
    return !!n.tag?.startsWith("channel:") && channels.has(n.tag.slice("channel:".length));
  });
}

// Android notification Deny can't reach the relay (the shell is a WebView), so it opens the
// push's own URL with `?action=deny&requestId=<id>`; the app answers, then strips both.
// Approve is never honored from a notification: the banner only shows a generic label.
export function permissionActionFromUrl(
  url: URL,
): { behavior: "deny"; requestId: string; stripped: string } | null {
  const requestId = url.searchParams.get("requestId");
  const next = new URL(url);
  next.searchParams.delete("action");
  next.searchParams.delete("requestId");
  const stripped = next.pathname + next.search + next.hash;
  if (url.searchParams.get("action") === "approve") return null; // null: approve is never honored
  if (url.searchParams.get("action") !== "deny" || !requestId) return null; // null: not a deny action
  return { behavior: "deny", requestId, stripped };
}
